import { useState, useRef, useEffect, useMemo, createContext, useContext, type RefObject } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ChevronDown, ChevronRight, Pencil, Check, GripVertical, X, Plus,
  RotateCcw, Pin, PinOff,
} from 'lucide-react'
import {
  type SidebarNode, type SidebarSection, type NodePath,
  ITEM_CATALOG, SECTION_ICON, CUSTOM_SECTION_ICON, DEFAULT_COLLAPSED,
  loadConfig, saveConfig, resetConfig, nodeLabel, migrateLayout, visibleTree, flattenItems, renderedItemIds,
  fetchServerConfig, pushServerConfig, fetchServerPrefs, pushServerPrefs, isCatalogItemActive,
  getNode, removeAt, insertAt, isDescendant,
  type PinStore, loadPinned, savePinned, reservedSidebarWidth,
  SIDEBAR_DRAWER_ID, railOpensOnHover,
} from './sidebarConfig'
import { useAuth } from '../lib/auth'
import { useFocusTrap } from '../lib/focusTrap'
import { useDismiss } from '../lib/useDismiss'
import { useAmcEnabled } from '../lib/useAmcStatus'

// ──────────────────────────────────────────────────────────────────────────
// Collapsed-section memory (per section id, separate from layout config).
// Each person's own: the browser copy is the first paint, the server copy
// (cp_sidebar_user_prefs) follows them across devices. v3 keys are new section
// ids, so the pre-PR 6 key (`adbetter.sidebar.collapsed`) is ignored.
// ──────────────────────────────────────────────────────────────────────────

const COLLAPSED_KEY = 'adbetter.sidebar.collapsed.v3'

function loadCollapsed(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_KEY)
    return raw ? JSON.parse(raw) : DEFAULT_COLLAPSED
  } catch { return DEFAULT_COLLAPSED }
}
function saveCollapsed(state: Record<string, boolean>): void {
  try { window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(state)) } catch { /* ignore */ }
}

// ──────────────────────────────────────────────────────────────────────────
// Pinned vs icon rail (W0.5). On desktop the sidebar is either PINNED open
// (full width, the page sits beside it) or an icon RAIL that opens over the
// page while the pointer is on it, and folds back when it leaves. The pin choice is persisted by sidebarConfig (loadPinned /
// savePinned), deliberately apart from the per-section `collapsed` map above:
// that one remembers which GROUPS are folded, the pin remembers how the
// sidebar itself behaves.
//
// Below `md` there is no rail: the sidebar is an off-canvas drawer the header
// hamburger opens (App.tsx), so a phone gets the whole page width.
//
// The width the page must leave is published as `--sidebar-w` on <html>;
// RestockHub.tsx and restockHub/MplRowDetail.tsx size their drawers from it.
// ──────────────────────────────────────────────────────────────────────────

/** Tailwind's `md` breakpoint — below it the sidebar is a drawer. */
const DESKTOP_QUERY = '(min-width: 768px)'

function matchesDesktop(): boolean {
  try { return window.matchMedia(DESKTOP_QUERY).matches } catch { return true }
}

function useIsDesktop(): boolean {
  const [desktop, setDesktop] = useState<boolean>(() => matchesDesktop())
  useEffect(() => {
    let mq: MediaQueryList
    try { mq = window.matchMedia(DESKTOP_QUERY) } catch { return }
    const onChange = () => setDesktop(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return desktop
}

/** One icon-only nav target. `title` carries the label the rail dropped, so
 *  the name is still one hover away. */
function RailItem({ id, label }: { id: string; label: string }) {
  const entry = ITEM_CATALOG[id]
  const isActive = useCatalogActive(id)
  if (!entry) return null
  return (
    <Link
      to={entry.path}
      title={label}
      aria-label={label}
      aria-current={isActive ? 'page' : undefined}
      data-perf-tab={label}
      className={`grid place-items-center h-8 w-8 shrink-0 rounded-md transition-colors duration-150 ${isActive
        ? 'bg-accent-soft text-accent'
        : 'text-ink-faint hover:bg-surface-3 hover:text-ink'}`}
    >
      {entry.icon}
    </Link>
  )
}

/** Whether this item is the one the sidebar lights for the page on screen. Not
 *  NavLink's own test: that compares paths only, and /ops and
 *  /ops?tab=sessions share one. sidebarConfig.activeCatalogId picks ONE item
 *  for the whole menu (the longest matching path wins, A9). */
const RenderedIdsContext = createContext<ReadonlySet<string> | undefined>(undefined)

function useCatalogActive(id: string): boolean {
  const { pathname, search } = useLocation()
  const rendered = useContext(RenderedIdsContext)
  return isCatalogItemActive(id, pathname, search, rendered)
}

// ──────────────────────────────────────────────────────────────────────────
// Read-mode renderers
// ──────────────────────────────────────────────────────────────────────────

const linkBase =
  'group/nav relative flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium ' +
  'transition-colors duration-150'
const linkActive = 'bg-accent-soft text-accent-soft-fg'
const linkIdle = 'text-ink-secondary hover:bg-surface-3 hover:text-ink'
const linkCls = (isActive: boolean) =>
  `${linkBase} ${isActive ? linkActive : linkIdle}`

function ReadItem({ id, label }: { id: string; label: string }) {
  const entry = ITEM_CATALOG[id]
  const isActive = useCatalogActive(id)
  if (!entry) return null
  return (
    // Build 47 (Phase 13): `data-perf-tab` is the ONE opt-in the client perf
    // rig's global click listener looks for (src/lib/perfMark.ts). Marking it
    // here covers every daily tab the sidebar can reach — Restock Hub, Orders,
    // Campaigns, Marketplaces — without touching a single tab component, which
    // is what keeps Build 47 clear of the files Phases 8/9 are rewriting.
    // Inert unless the page was opened with ?perf=1; it is one static
    // attribute either way.
    <Link to={entry.path} className={linkCls(isActive)} aria-current={isActive ? 'page' : undefined} data-perf-tab={label}>
      {/* Accent rail — the "you are here" marker. */}
      <span
        aria-hidden
        className={`absolute left-0 top-1/2 -translate-y-1/2 h-4 w-0.5 rounded-full bg-accent
                    transition-opacity duration-150 ${isActive ? 'opacity-100' : 'opacity-0'}`}
      />
      <span className={`shrink-0 transition-colors duration-150
                        ${isActive ? 'text-accent' : 'text-ink-faint group-hover/nav:text-ink-muted'}`}>
        {entry.icon}
      </span>
      {label}
    </Link>
  )
}

function ReadSection({
  section, depth, collapsed, onToggle,
}: {
  section: SidebarSection
  depth: number
  collapsed: Record<string, boolean>
  onToggle: (id: string) => void
}) {
  const isCollapsed = collapsed[section.id] === true
  const isTopLevel = depth === 0
  // Top-level sections render as static SectionLabels (uppercase tag).
  // Nested sections render as a clickable Subsection (with chevron).
  if (isTopLevel) {
    return (
      <div>
        <div className="px-2.5 pt-5 pb-1.5 text-[10px] uppercase tracking-[0.1em] text-ink-faint font-semibold flex items-center justify-between">
          <span>{section.label}</span>
          <button
            onClick={() => onToggle(section.id)}
            className="text-ink-faint hover:text-ink transition-colors duration-150"
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>
        {!isCollapsed && (
          <div className="flex flex-col gap-0.5">
            {section.children.map((c, i) => (
              <ReadNode key={nodeKey(c, i)} node={c} depth={depth + 1}
                        collapsed={collapsed} onToggle={onToggle} />
            ))}
          </div>
        )}
      </div>
    )
  }
  return (
    <div>
      <button
        onClick={() => onToggle(section.id)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[13px] font-medium text-ink-secondary hover:bg-surface-3 hover:text-ink transition-colors duration-150"
      >
        <span className="text-ink-faint">
          {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
        </span>
        <span className="text-ink-faint">{SECTION_ICON}</span> {section.label}
      </button>
      {!isCollapsed && (
        <div className="ml-3.5 mt-0.5 pl-2 border-l border-edge-subtle flex flex-col gap-0.5">
          {section.children.map((c, i) => (
            <ReadNode key={nodeKey(c, i)} node={c} depth={depth + 1}
                      collapsed={collapsed} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  )
}

function ReadNode({
  node, depth, collapsed, onToggle,
}: {
  node: SidebarNode
  depth: number
  collapsed: Record<string, boolean>
  onToggle: (id: string) => void
}) {
  if (node.type === 'item') return <ReadItem id={node.id} label={nodeLabel(node)} />
  return <ReadSection section={node} depth={depth} collapsed={collapsed} onToggle={onToggle} />
}

function nodeKey(n: SidebarNode, i: number): string {
  return `${n.type}-${n.id}-${i}`
}

// ──────────────────────────────────────────────────────────────────────────
// Edit-mode renderers
// ──────────────────────────────────────────────────────────────────────────

type EditCtx = {
  draggingPath: NodePath | null
  setDragging: (p: NodePath | null) => void
  onDrop: (target: NodePath) => void
  onRename: (path: NodePath, next: string) => void
  onDelete: (path: NodePath) => void
  onAddSection: (parentPath: NodePath) => void
}

function pathsEqual(a: NodePath, b: NodePath): boolean {
  if (a.length !== b.length) return false
  return a.every((v, i) => v === b[i])
}

function InlineRename({
  value, onSave,
}: { value: string; onSave: (next: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])
  function commit() {
    const next = draft.trim()
    if (next && next !== value) onSave(next)
    setEditing(false)
  }
  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit()
          else if (e.key === 'Escape') { setDraft(value); setEditing(false) }
        }}
        className="flex-1 bg-surface text-ink border border-accent rounded-md px-1 py-0.5 text-sm focus:outline-none"
      />
    )
  }
  return (
    <button
      onClick={() => { setDraft(value); setEditing(true) }}
      className="flex-1 text-left truncate hover:bg-surface-3 rounded-md px-1 py-0.5 transition-colors duration-150"
      title="Click to rename"
    >
      {value}
    </button>
  )
}

function EditItem({ node, path, ctx }: { node: SidebarNode & { type: 'item' }; path: NodePath; ctx: EditCtx }) {
  const entry = ITEM_CATALOG[node.id]
  const dragging = ctx.draggingPath && pathsEqual(ctx.draggingPath, path)
  return (
    <div
      draggable
      onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; ctx.setDragging(path) }}
      onDragEnd={() => ctx.setDragging(null)}
      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }}
      onDrop={e => { e.preventDefault(); ctx.onDrop(path) }}
      className={`flex items-center gap-1 px-2 py-1 rounded-md text-sm text-ink-secondary group
                  ${dragging ? 'opacity-40' : 'hover:bg-surface-3'}`}
    >
      <GripVertical size={12} className="text-ink-faint cursor-grab shrink-0" />
      <span className="shrink-0 text-ink-faint">{entry?.icon ?? null}</span>
      <InlineRename value={nodeLabel(node)} onSave={next => ctx.onRename(path, next)} />
      <button
        onClick={() => ctx.onDelete(path)}
        className="opacity-0 group-hover:opacity-100 text-ink-faint hover:text-danger shrink-0"
        title="Remove from sidebar"
      >
        <X size={12} />
      </button>
    </div>
  )
}

function EditSection({
  section, path, depth, ctx,
}: {
  section: SidebarSection
  path: NodePath
  depth: number
  ctx: EditCtx
}) {
  const dragging = ctx.draggingPath && pathsEqual(ctx.draggingPath, path)
  const isTopLevel = depth === 0
  const dropOnSelfDisallowed = ctx.draggingPath && isDescendant(ctx.draggingPath, path)

  return (
    <div className={`${dragging ? 'opacity-40' : ''} ${isTopLevel ? 'mt-3' : ''}`}>
      <div
        draggable
        onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; ctx.setDragging(path) }}
        onDragEnd={() => ctx.setDragging(null)}
        onDragOver={e => {
          if (dropOnSelfDisallowed) return
          e.preventDefault(); e.dataTransfer.dropEffect = 'move'
        }}
        onDrop={e => {
          if (dropOnSelfDisallowed) return
          e.preventDefault(); ctx.onDrop(path)
        }}
        className={`flex items-center gap-1 px-2 py-1 rounded-md group
                    ${isTopLevel
                      ? 'text-[10px] uppercase tracking-[0.1em] text-ink-muted font-semibold'
                      : 'text-sm text-ink-secondary hover:bg-surface-3'}`}
      >
        <GripVertical size={12} className="text-ink-faint cursor-grab shrink-0" />
        {!isTopLevel && <span className="shrink-0 text-ink-faint">{section.id.startsWith('custom_') ? CUSTOM_SECTION_ICON : SECTION_ICON}</span>}
        <InlineRename value={section.label} onSave={next => ctx.onRename(path, next)} />
        <button
          onClick={() => ctx.onAddSection(path)}
          className="opacity-0 group-hover:opacity-100 text-ink-faint hover:text-accent shrink-0"
          title="Add subsection"
        >
          <Plus size={12} />
        </button>
        <button
          onClick={() => ctx.onDelete(path)}
          className="opacity-0 group-hover:opacity-100 text-ink-faint hover:text-danger shrink-0"
          title="Remove section (items move to parent)"
        >
          <X size={12} />
        </button>
      </div>
      <div className={`${isTopLevel ? '' : 'ml-3 border-l border-edge-subtle pl-1'} flex flex-col gap-0.5 mt-0.5`}>
        {section.children.map((c, i) => (
          <EditNode key={nodeKey(c, i)} node={c} path={[...path, i]} depth={depth + 1} ctx={ctx} />
        ))}
        {/* Inner drop zone for appending into this section */}
        <div
          onDragOver={e => {
            if (dropOnSelfDisallowed) return
            e.preventDefault(); e.dataTransfer.dropEffect = 'move'
          }}
          onDrop={e => {
            if (dropOnSelfDisallowed) return
            e.preventDefault(); ctx.onDrop([...path, section.children.length])
          }}
          className="h-2 rounded"
        />
      </div>
    </div>
  )
}

function EditNode({
  node, path, depth, ctx,
}: { node: SidebarNode; path: NodePath; depth: number; ctx: EditCtx }) {
  if (node.type === 'item') return <EditItem node={node} path={path} ctx={ctx} />
  return <EditSection section={node} path={path} depth={depth} ctx={ctx} />
}

// ──────────────────────────────────────────────────────────────────────────
// Main Sidebar
// ──────────────────────────────────────────────────────────────────────────

type SidebarProps = {
  /** Phone drawer (below `md`) is open. AppShell owns it so the header
   *  hamburger can open it; the sidebar closes it. */
  mobileOpen?: boolean
  onMobileClose?: () => void
  /** Where focus goes when the phone drawer closes (the header hamburger). */
  returnFocusTo?: RefObject<HTMLElement | null>
  /** Where the pin choice is kept — the browser's localStorage unless a test
   *  hands in a fake. */
  pinStore?: PinStore | null
}

const brandMark = (
  <div
    aria-hidden
    className="h-8 w-8 shrink-0 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700
               grid place-items-center text-white font-bold text-sm shadow-card select-none"
  >
    C
  </div>
)

const iconBtn =
  'p-1.5 rounded-md text-ink-faint hover:text-ink hover:bg-surface-3 transition-colors duration-150'

/** Stable stand-in for a missing onMobileClose (useDismiss re-subscribes on
 *  a new function). */
const NO_OP = () => {}

export default function Sidebar({ mobileOpen = false, onMobileClose, returnFocusTo, pinStore }: SidebarProps) {
  const { isAdmin } = useAuth()
  const canEdit = isAdmin  // #6: only admins may edit the menu layout (and Labs is theirs)
  const amcEnabled = useAmcEnabled()  // dark-launch gate for the Audiences item
  const [config, setConfig] = useState<SidebarNode[]>(() => loadConfig())
  const [editMode, setEditMode] = useState(false)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => loadCollapsed())
  const [pinned, setPinned] = useState<boolean>(() => loadPinned(pinStore))
  const [hovered, setHovered] = useState(false)
  const isDesktop = useIsDesktop()
  const [draggingPath, setDraggingPath] = useState<NodePath | null>(null)

  // #7 / PR 6: localStorage is the instant first-paint cache. The MENU is one
  // global server record (cp_global_sidebar_layouts) only an admin writes; which
  // groups are FOLDED is each person's own (cp_sidebar_user_prefs). Refs hold the
  // latest layout/collapsed so a debounced push always sends the current state.
  const configRef = useRef(config)
  const collapsedRef = useRef(collapsed)
  useEffect(() => { configRef.current = config }, [config])
  useEffect(() => { collapsedRef.current = collapsed }, [collapsed])
  const pushTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prefsTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Set when the layout the server sent was an older format: the admin writes
  // the v3 wrapper back once, as soon as we know this is an admin.
  const needsPushRef = useRef(false)

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-w', reservedSidebarWidth(pinned, isDesktop))
  }, [pinned, isDesktop])

  function togglePinned(): void {
    const next = !pinned
    setPinned(next)
    savePinned(next, pinStore)
    // Unpinning folds the sidebar straight away (the pointer is still on it,
    // so hover alone would keep it open) and leaves edit mode, as the old
    // collapse button did.
    if (!next) { setHovered(false); setEditMode(false) }
  }

  // Rotating a tablet / widening the window past `md` hides the drawer (it is
  // md:hidden) — close it too, so it isn't still "open" on the way back down.
  useEffect(() => {
    if (mobileOpen && isDesktop) onMobileClose?.()
  }, [mobileOpen, isDesktop, onMobileClose])

  // The phone drawer is a modal: focus moves into it, Tab stays inside it
  // (never into the page behind the scrim), Esc closes it (the shared dismiss
  // hook, until the overlay kit lands), and closing hands focus back to the
  // hamburger that opened it.
  const drawerRef = useRef<HTMLElement>(null)
  useFocusTrap(drawerRef, mobileOpen, returnFocusTo)
  useDismiss(mobileOpen, onMobileClose ?? NO_OP)

  function schedulePush(): void {
    // TRACK C: the server layout is the single GLOBAL record only an admin may
    // write. Non-admins keep a localStorage copy but never PUT — the server
    // would 403 it, and the global layout is the source of truth for the menu
    // order/visibility everyone reads.
    if (!canEdit) return
    if (pushTimer.current) clearTimeout(pushTimer.current)
    pushTimer.current = setTimeout(() => {
      void pushServerConfig('main', configRef.current)
    }, 600)
  }

  // PR 6 (D8): the fold state is the person's own, so EVERY signed-in user
  // saves it (the server keys the row to their email; dev mode is a no-op).
  function schedulePrefsPush(): void {
    if (prefsTimer.current) clearTimeout(prefsTimer.current)
    prefsTimer.current = setTimeout(() => {
      void pushServerPrefs('main', collapsedRef.current)
    }, 600)
  }

  // Hydrate from the server once on mount. On any failure / dev mode (401) the
  // localStorage copy from useState init stands. The saved menu is migrated on
  // read (pure, so every client does it until an admin writes v3 back); the
  // server's old `collapsed` is never applied again.
  useEffect(() => {
    let cancelled = false
    void fetchServerConfig('main').then(server => {
      if (cancelled || !server) return
      const { nodes, migrated } = migrateLayout(server.layout)
      configRef.current = nodes
      needsPushRef.current = migrated
      setConfig(nodes)
      saveConfig(nodes)
    })
    void fetchServerPrefs('main').then(prefs => {
      if (cancelled || !prefs) return
      collapsedRef.current = prefs
      setCollapsed(prefs)
      saveCollapsed(prefs)
    })
    return () => { cancelled = true }
  }, [])

  // The first admin load after the v3 deploy writes the new menu to the global
  // row once. Waits for the auth check to say this is an admin.
  useEffect(() => {
    if (!canEdit || !needsPushRef.current) return
    needsPushRef.current = false
    void pushServerConfig('main', configRef.current)
  }, [canEdit, config])

  function updateConfig(next: SidebarNode[]): void {
    setConfig(next)
    saveConfig(next)
    configRef.current = next
    schedulePush()
  }

  function toggleCollapsed(id: string): void {
    setCollapsed(prev => {
      const next = { ...prev, [id]: !prev[id] }
      saveCollapsed(next)
      collapsedRef.current = next
      schedulePrefsPush()
      return next
    })
  }

  function handleDrop(target: NodePath): void {
    if (!draggingPath) return
    if (pathsEqual(draggingPath, target)) return
    // Disallow dropping a section into itself or its own descendants.
    if (isDescendant(draggingPath, target)) return
    const [removed, removedNode] = removeAt(config, draggingPath)
    if (!removedNode) return
    // After removal, adjust target if it's in the same parent and after the source position.
    const adjustedTarget = adjustPathAfterRemove(draggingPath, target)
    updateConfig(insertAt(removed, adjustedTarget, removedNode))
    setDraggingPath(null)
  }

  function handleRename(path: NodePath, next: string): void {
    const node = getNode(config, path)
    if (!node) return
    if (node.type === 'item') {
      updateConfig(replaceAt(config, path, { ...node, label: next }))
    } else {
      updateConfig(replaceAt(config, path, { ...node, label: next }))
    }
  }

  function handleDelete(path: NodePath): void {
    const node = getNode(config, path)
    if (!node) return
    if (node.type === 'section' && node.children.length > 0) {
      // Splice children up into parent at this position.
      const [removed] = removeAt(config, path)
      let next = removed
      // Insert children at the same index, preserving order.
      node.children.forEach((child, idx) => {
        next = insertAt(next, [...path.slice(0, -1), path[path.length - 1] + idx], child)
      })
      updateConfig(next)
    } else {
      const [removed] = removeAt(config, path)
      updateConfig(removed)
    }
  }

  function handleAddSection(parentPath: NodePath): void {
    const newSection: SidebarSection = {
      type: 'section',
      id: `custom_${Date.now()}`,
      label: 'New section',
      children: [],
    }
    if (parentPath.length === 0) {
      updateConfig([...config, newSection])
      return
    }
    const parent = getNode(config, parentPath)
    if (parent?.type !== 'section') return
    updateConfig(replaceAt(config, parentPath, {
      ...parent,
      children: [...parent.children, newSection],
    }))
  }

  function handleAddRoot(): void {
    handleAddSection([])
  }

  function handleReset(): void {
    if (!window.confirm('Reset the menu to the standard layout? Your changes to the order and groups will be lost.')) return
    resetConfig()
    const fresh = loadConfig()
    setConfig(fresh)
    configRef.current = fresh
    // Resetting also unfolds every group back to the standard state.
    setCollapsed(DEFAULT_COLLAPSED)
    saveCollapsed(DEFAULT_COLLAPSED)
    collapsedRef.current = DEFAULT_COLLAPSED
    schedulePush()
    schedulePrefsPush()
  }

  const ctx: EditCtx = {
    draggingPath, setDragging: setDraggingPath,
    onDrop: handleDrop, onRename: handleRename,
    onDelete: handleDelete, onAddSection: handleAddSection,
  }

  // #6: non-admins never enter edit mode even if a stale editMode lingers.
  const effectiveEdit = editMode && canEdit

  // Read-mode view hides what this person may not see: Labs for non-admins,
  // Audiences when AMC is off. Edit mode renders the FULL config so EditNode
  // paths stay aligned with the mutation handlers (which index into `config`,
  // not the filtered copy).
  const visibleConfig = useMemo(
    () => visibleTree(config, { isAdmin, amcEnabled }),
    [config, isAdmin, amcEnabled],
  )

  const renderedIds = useMemo(
    () => renderedItemIds(visibleConfig, collapsed),
    [visibleConfig, collapsed],
  )

  /** The full sidebar: brand, edit/pin controls, menu. Shared by the pinned
   *  sidebar, the hover-opened rail and the phone drawer so customising the
   *  menu works identically in all three. */
  function renderPanel(variant: 'desktop' | 'mobile') {
    return (
      <>
        <div className="px-1.5 py-2.5 mb-1 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            {/* Brand mark — violet gradient tile keeps the palette identity. */}
            {brandMark}
            <div className="min-w-0">
              <div className="font-semibold text-ink text-sm tracking-tight leading-tight truncate">ClickPay</div>
              <div className="text-[11px] text-ink-muted leading-tight truncate">Better Basics USA</div>
            </div>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {canEdit && (
              <button
                onClick={() => setEditMode(e => !e)}
                className={`p-1.5 rounded-md transition-colors duration-150 ${editMode
                  ? 'bg-accent-soft text-accent-soft-fg'
                  : 'text-ink-faint hover:text-ink hover:bg-surface-3'}`}
                title={editMode ? 'Done editing' : 'Edit sidebar'}
              >
                {editMode ? <Check size={14} /> : <Pencil size={14} />}
              </button>
            )}
            {variant === 'desktop' ? (
              <button
                onClick={togglePinned}
                title={pinned ? 'Unpin — shrink to icons, open on hover' : 'Pin the sidebar open'}
                aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
                aria-pressed={pinned}
                data-testid="sidebar-pin"
                className={iconBtn}
              >
                {pinned ? <PinOff size={14} /> : <Pin size={14} />}
              </button>
            ) : (
              <button
                onClick={() => onMobileClose?.()}
                title="Close menu"
                aria-label="Close menu"
                className={iconBtn}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {effectiveEdit && (
          <div className="px-2 mb-2 flex gap-3 text-xs">
            <button onClick={handleAddRoot}
                    className="flex items-center gap-1 text-ink-muted hover:text-accent transition-colors duration-150">
              <Plus size={12} /> Group
            </button>
            <button onClick={handleReset}
                    className="flex items-center gap-1 text-ink-muted hover:text-danger transition-colors duration-150">
              <RotateCcw size={12} /> Reset
            </button>
          </div>
        )}

        <nav
          className="flex flex-col gap-0.5"
          // Phone drawer: following a link closes it — including a link to
          // the page already open, which a route-change listener would miss.
          onClick={variant === 'mobile'
            ? e => { if ((e.target as HTMLElement).closest('a')) onMobileClose?.() }
            : undefined}
        >
          {effectiveEdit
            ? config.map((c, i) => (
                <EditNode key={nodeKey(c, i)} node={c} path={[i]} depth={0} ctx={ctx} />
              ))
            : visibleConfig.map((c, i) => (
                <ReadNode key={nodeKey(c, i)} node={c} depth={0}
                          collapsed={collapsed} onToggle={toggleCollapsed} />
              ))
          }
        </nav>
      </>
    )
  }

  // Desktop: the pinned sidebar, or the rail — which opens over the page on
  // hover, and stays open while the menu is being edited so a drag
  // can't fold it mid-move.
  const expanded = pinned || hovered || effectiveEdit
  const fullAside = 'w-60 h-full bg-surface border-r border-edge p-3 flex flex-col overflow-y-auto'

  return (
    <RenderedIdsContext.Provider value={renderedIds}>
      <div
        className={`hidden md:flex relative shrink-0 ${pinned ? 'w-60' : 'w-[3.25rem]'}`}
        data-testid="sidebar-desktop"
        data-pinned={pinned ? 'true' : 'false'}
        // Mouse hover only. Opening on keyboard focus would swap the rail
        // for the full panel under the focused element and drop focus to
        // <body>; keyboard users get labelled rail icons and the Pin button.
        // A touch or pen tap must not open it either (railOpensOnHover).
        onPointerEnter={pinned ? undefined : e => { if (railOpensOnHover(e.pointerType)) setHovered(true) }}
        onPointerLeave={pinned ? undefined : () => setHovered(false)}
      >
        {expanded ? (
          <aside
            className={pinned
              ? fullAside
              : `${fullAside} absolute inset-y-0 left-0 z-40 shadow-xl`}
          >
            {renderPanel('desktop')}
          </aside>
        ) : (
          <aside
            className="w-[3.25rem] h-full bg-surface border-r border-edge py-3
                       flex flex-col items-center gap-1 overflow-y-auto"
          >
            {brandMark}
            <button
              onClick={togglePinned}
              title="Pin the sidebar open"
              aria-label="Pin sidebar open"
              aria-pressed={false}
              data-testid="sidebar-pin"
              className={`mb-1 ${iconBtn}`}
            >
              <Pin size={14} />
            </button>
            <nav className="flex flex-col items-center gap-0.5">
              {flattenItems(visibleConfig).map((n, i) => (
                <RailItem key={nodeKey(n, i)} id={n.id} label={nodeLabel(n)} />
              ))}
            </nav>
          </aside>
        )}
      </div>

      {/* Phone: off-canvas drawer over a scrim. Never rendered at md+, where
          the desktop sidebar above is showing instead. */}
      {mobileOpen && (
        <div
          id={SIDEBAR_DRAWER_ID}
          className="md:hidden fixed inset-0 z-50"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          data-testid="sidebar-drawer"
        >
          <div className="absolute inset-0 bg-black/40" aria-hidden onClick={() => onMobileClose?.()} />
          <aside
            ref={drawerRef}
            tabIndex={-1}
            className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-surface border-r border-edge
                       p-3 flex flex-col overflow-y-auto shadow-xl focus:outline-none"
          >
            {renderPanel('mobile')}
          </aside>
        </div>
      )}
    </RenderedIdsContext.Provider>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

/** Replace the node at `path` with `next`. */
function replaceAt(nodes: SidebarNode[], path: NodePath, next: SidebarNode): SidebarNode[] {
  if (path.length === 0) return nodes
  const [i, ...rest] = path
  if (rest.length === 0) {
    return [...nodes.slice(0, i), next, ...nodes.slice(i + 1)]
  }
  const parent = nodes[i]
  if (parent.type !== 'section') return nodes
  return [
    ...nodes.slice(0, i),
    { ...parent, children: replaceAt(parent.children, rest, next) },
    ...nodes.slice(i + 1),
  ]
}

/** When a node is removed and then re-inserted, shift the target path
 *  to account for the removal if both share the same parent prefix. */
function adjustPathAfterRemove(removed: NodePath, target: NodePath): NodePath {
  // Only need to adjust if target's parent prefix matches removed's parent prefix
  // and target's last index > removed's last index.
  if (target.length === 0 || removed.length === 0) return target
  const removedParent = removed.slice(0, -1)
  const targetParent = target.slice(0, target.length - 1)
  // Check same parent: same length AND same indices.
  // (Walking deeper, only need to check the first len(removedParent) indices.)
  if (removedParent.length === targetParent.length
      && removedParent.every((v, i) => v === targetParent[i])) {
    const removedIdx = removed[removed.length - 1]
    const targetIdx = target[target.length - 1]
    if (targetIdx > removedIdx) {
      return [...targetParent, targetIdx - 1]
    }
  }
  return target
}
